import type { RefObject } from "react"
import type { Message } from "./types"
import { ChatMessage, type FeedbackScope } from "./ChatMessage"
import LoadingSpinner from "@/components/shared/LoadingSpinner.tsx";

interface ChatMessagesProps {
  messages: Message[]
  messagesEndRef: RefObject<HTMLDivElement | null>
  sessionId: string
  loadingHistory?: boolean
  /**
   * Callback to submit feedback for a message.
   *
   * @param message - The message being rated.
   * @param feedbackType - "positive" or "negative"
   * @param comment - Free-text comment from the user.
   * @param messageIndex - Index of the message in the array.
   * @param scope - What the feedback targets: whole "message", a "highlight", or a "tool".
   * @param messageOverride - When provided, use this string as the feedback message
   *   (e.g. highlighted text or tool summary) instead of the full message content.
   */
  onFeedbackSubmit: (
    message: Message,
    feedbackType: "positive" | "negative",
    comment: string,
    messageIndex: number,
    scope: FeedbackScope,
    messageOverride?: string
  ) => Promise<void>
}

export function ChatMessages({
  messages,
  messagesEndRef,
  sessionId,
  loadingHistory,
  onFeedbackSubmit,
}: ChatMessagesProps) {
  return (
    <div
      className={`h-full lg:px-12 space-y-4 w-full p-4 ${
        messages.length > 0 ? "overflow-y-auto" : "overflow-hidden"
      }`}
    >
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {loadingHistory ? <LoadingSpinner loadingContent={"Loading conversation..."}/> : "New conversation"}
        </div>
      ) : (
        messages.map((message, index) => (
          <ChatMessage
            key={index}
            message={message}
            sessionId={sessionId}
            onFeedbackSubmit={async (feedbackType, comment, scope, messageOverride) => {
              await onFeedbackSubmit(message, feedbackType, comment, index, scope, messageOverride)
            }}
          />
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  )
}
